import React, { JSX, useContext, useEffect, useState } from "react";
import { orderBy } from "lodash";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters";
import { BenchmarkingNetwork, BenchmarkingNetworkCounty } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { QESButton, QESCheckbox } from "@app/shared/qes-standards";
import { ColumnConfig, SortDirections, Waffles, printNumberAsString, wafflesKeyColumnName } from "@app/shared/waffles";
import { TextAlignment, WafflesThemes } from "@app/shared/waffles/exports";

interface NetworkFlyoutCountiesTableProps {
  data: BenchmarkingNetworkCounty[];
  contract: BenchmarkingNetwork;
  isYourNetwork: boolean;
  loading: boolean;
  onApplyServiceAreaClick: (countyIds: number[], contract: BenchmarkingNetwork) => void;
}

interface NetworkFlyoutCountiesTableData {
  countyName: string;
  countyStatus: RegionSetStatusType;
  membershipCount: string;
  stateAbbreviation: string;
  [wafflesKeyColumnName]: string;
}

const NetworkFlyoutCountiesTable = (props: NetworkFlyoutCountiesTableProps): JSX.Element => {
  const [allActiveCountiesChecked, setAllActiveCountiesChecked] = useState<boolean>(false);
  const [tableData, setTableData] = useState<NetworkFlyoutCountiesTableData[]>([]);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);

  useEffect(() => {
    if (!!props.data) {
      let data: BenchmarkingNetworkCounty[];

      if (allActiveCountiesChecked) {
        data = props.data;
      } else {
        data = props.data.filter(x => benchmarkingFiltersContext.displayedPrimaryFilters.countyIds.includes(x.countyId));
      }

      /* eslint-disable sort-keys */
      const newTableData: NetworkFlyoutCountiesTableData[] = data.map((county) => {
        return {
          [wafflesKeyColumnName]: county.countyId.toString(),
          countyName: county.countyName,
          stateAbbreviation: county.stateAbbreviation,
          countyStatus: county.countyStatus,
          membershipCount: county.membershipCount === 0 ? '*' : printNumberAsString(county.membershipCount, "---"),
        }
      });
      /* eslint-enable sort-keys */

      setTableData(newTableData);
    }
  }, [props.data, allActiveCountiesChecked]);

  const columnConfig: ColumnConfig = {
    countyName: {
      alignment: TextAlignment.Left,
      displayText: "County Name",
      noResize: true,
      sizeContentToFit: true,
      testId: "county-name",
    },
    countyStatus: {
      alignment: TextAlignment.Left,
      displayText: "County Status",
      hidden: !props.isYourNetwork,
      noResize: true,
      sizeContentToFit: true,
      testId: "county-status",
    },
    membershipCount: {
      alignment: TextAlignment.Left,
      displayText: "Membership Count",
      noResize: true,
      sizeContentToFit: true,
      testId: "membership-count",
    },
    stateAbbreviation: {
      alignment: TextAlignment.Left,
      displayText: "State",
      noResize: true,
      sizeContentToFit: true,
      testId: "state-abbreviation",
    },
  };

  const customSort = (
    records: NetworkFlyoutCountiesTableData[],
    column: keyof NetworkFlyoutCountiesTableData,
    sortDirection: SortDirections
  ): NetworkFlyoutCountiesTableData[] => {
    let sortedData: NetworkFlyoutCountiesTableData[] = [];

    switch (column) {
      case "countyName":
      case "countyStatus":
      case "stateAbbreviation":
        sortedData = orderBy(records, (d) => d[column], sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
      case "membershipCount":
        sortedData = orderBy(records, (d) => d.membershipCount === '*' ? 0 : parseInt(d.membershipCount.replace(/,/g, "")), sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
      default:
        break;
    }

    return sortedData;
  }

  return (
    <React.Fragment>
      <div className="row">
        <div className="col-12">
          <div className="d-flex align-items-center mb-3">
            <div className="d-inline-block">
              <QESCheckbox
                checked={allActiveCountiesChecked}
                containerClassName="mb-0"
                disabled={props.loading || !tableData.length}
                label="All active counties for contract"
                testId={`network-benchmarking-dashboard--h-contract-flyout__active-counties-checkbox-${props.contract?.contractId}`}
                onChange={() => setAllActiveCountiesChecked(!allActiveCountiesChecked)}
              />
            </div>

            <QESButton
              disabled={props.loading || !tableData.length}
              qesTestId={`network-benchmarking-dashboard--h-contract-flyout__set-service-area-button-${props.contract?.contractId}`}
              qesType="secondary"
              onClick={() => props.onApplyServiceAreaClick(tableData.map(x => parseInt(x.key)), props.contract)}
              className="pb-0"
            >
              Set service area filter
            </QESButton>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 dashboard__scrollable">
          <Waffles<NetworkFlyoutCountiesTableData>
            columns={columnConfig}
            customSort={customSort}
            data={tableData}
            fullWidth={true}
            testId={`network-benchmarking-dashboard--h-contract-flyout__counties-table-${props.contract?.contractId}`}
            theme={WafflesThemes.Simple}
          />
        </div>
      </div>
    </React.Fragment>
  )
}

export default NetworkFlyoutCountiesTable;
